import React, { useCallback, useEffect, useMemo, useState } from "react";
import DispatchNav, { DispatchNavItem, NAV_ITEMS } from "../DispatchNav";
import Overview from "../Overview";
import Notes from "../Notes";
import Photos from "../Photos";
import { DispatchPageContainer, InnerPageWrapper, DispatchButtonWrapper } from "./styled";
import { Flex } from "theme-ui";
import { themevals } from "theme/themevals";
import BasicModal from "components/ui/BasicModal";
import { WarningIcon, SubmitIcon } from "components/svg";
import BasicButton from "components/forms/BasicButton";
import P from "components/typography/P";
import { completeDispatch, markDispatchOnSite } from "api/Projects";
import { useToast } from "components/toast";
import { trackActivity } from "api/ActivityTracker";

type PageProps = {
  dispatch?: any;
  onDispatchUpdated?: () => Promise<void> | void;
};

const Page: React.FC<PageProps> = ({ dispatch, onDispatchUpdated }) => {
  const [activeItem, setActiveItem] = useState<DispatchNavItem>(NAV_ITEMS[0]);
  const [confirmNotesModalOpen, setConfirmNotesModalOpen] = useState<boolean>(false);
  const [confirmPhotosModalOpen, setConfirmPhotosModalOpen] = useState<boolean>(false);
  const [onSiteSubmitting, setOnSiteSubmitting] = useState<boolean>(false);
  const toast = useToast();
  const notesCount = Array.isArray(dispatch?.notes) ? dispatch.notes.length : 0;
  const hasUnreadNotes = notesCount > 0 && dispatch?.driverNotesRead !== true;
  const hasRequiredPhotos = useMemo(() => {
    const purchaseOrderId = dispatch?.purchaseOrder?.id;
    if (!purchaseOrderId) {
      return true;
    }

    if (!Array.isArray(dispatch?.photos)) {
      return false;
    }

    return dispatch.photos.some(
      (photo: any) => photo?.purchaseOrder?.id === purchaseOrderId,
    );
  }, [dispatch]);

  const _cancelConfirmModal = () => {
    setConfirmNotesModalOpen(false);
  };

  const _goToNotes = () => {
    setConfirmNotesModalOpen(false);
    setActiveItem("Notes");
    if (dispatch?.id) {
      void trackActivity({
        module: "dispatches",
        action: "click_go_to_notes",
        actionType: "click",
        userId: null,
        companyId: dispatch?.company?.id ?? dispatch?.project?.company?.id ?? null,
        entityType: "dispatch",
        entityId: dispatch.id,
        metadata: {
          projectId: dispatch?.project?.id ?? null,
          purchaseOrderId: dispatch?.purchaseOrder?.id ?? null,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
    }
  };

  const _goToPhotos = () => {
    setConfirmPhotosModalOpen(false);
    setActiveItem("Photos");
    if (dispatch?.id) {
      void trackActivity({
        module: "dispatches",
        action: "click_go_to_photos",
        actionType: "click",
        userId: null,
        companyId: dispatch?.company?.id ?? dispatch?.project?.company?.id ?? null,
        entityType: "dispatch",
        entityId: dispatch.id,
        metadata: {
          projectId: dispatch?.project?.id ?? null,
          purchaseOrderId: dispatch?.purchaseOrder?.id ?? null,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
    }
  };

  const handleIAmHereClick = async () => {
    if (!dispatch?.id) {
      return;
    }

    try {
      setOnSiteSubmitting(true);

      if (dispatch.status === 4) {
        if (!hasRequiredPhotos) {
          setOnSiteSubmitting(false);
          setConfirmPhotosModalOpen(true);
          return;
        }

        await completeDispatch({ id: dispatch.id });
        void trackActivity({
          module: "dispatches",
          action: "mark_dispatch_complete",
          actionType: "update",
          userId: null,
          companyId: dispatch?.company?.id ?? dispatch?.project?.company?.id ?? null,
          entityType: "dispatch",
          entityId: dispatch.id,
          metadata: {
            projectId: dispatch?.project?.id ?? null,
            purchaseOrderId: dispatch?.purchaseOrder?.id ?? null,
          },
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      } else {
        if (hasUnreadNotes) {
          setOnSiteSubmitting(false);
          setConfirmNotesModalOpen(true);
          return;
        }

        await markDispatchOnSite({ id: dispatch.id });
        void trackActivity({
          module: "dispatches",
          action: "mark_dispatch_on_site",
          actionType: "update",
          userId: null,
          companyId: dispatch?.company?.id ?? dispatch?.project?.company?.id ?? null,
          entityType: "dispatch",
          entityId: dispatch.id,
          metadata: {
            projectId: dispatch?.project?.id ?? null,
            purchaseOrderId: dispatch?.purchaseOrder?.id ?? null,
          },
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      }

      if (typeof onDispatchUpdated === "function") {
        await onDispatchUpdated();
      }
      toast.add("Dispatch updated successfully.", "var(--theme-ui-colors-success)");
    } catch (error) {
      console.error("Failed to update dispatch status", error);
      toast.add("There was an error updating the dispatch.", "var(--theme-ui-colors-danger)");
    } finally {
      setOnSiteSubmitting(false);
    }
  };

  const handleNotesViewed = useCallback(async () => {
    if (typeof onDispatchUpdated === "function") {
      await onDispatchUpdated();
    }
  }, [onDispatchUpdated]);

  useEffect(() => {
    if (activeItem === "Notes") {
      setConfirmNotesModalOpen(false);
    }
    if (activeItem === "Photos") {
      setConfirmPhotosModalOpen(false);
    }
  }, [activeItem]);

  const handlePhotoUploaded = useCallback(async () => {
    if (typeof onDispatchUpdated === "function") {
      await onDispatchUpdated();
    }
  }, [onDispatchUpdated]);

  const renderActiveSection = () => {
    switch (activeItem) {
      case "Notes":
        return <Notes dispatch={dispatch} onDispatchViewed={handleNotesViewed} />;
      case "Photos":
        return <Photos dispatch={dispatch} onPhotoUploaded={handlePhotoUploaded} />;
      case "Overview":
      default:
        return <Overview dispatch={dispatch} />;
    }
  };

  return (
    <DispatchPageContainer>
      <InnerPageWrapper>
        <DispatchNav
          activeItem={activeItem}
          onSelect={(item) => {
            if (item === "Notes" && activeItem !== "Notes" && dispatch?.id) {
              void trackActivity({
                module: "dispatches",
                action: "dispatch_note_tab_click",
                actionType: "click",
                userId: null,
                companyId: dispatch?.company?.id ?? dispatch?.project?.company?.id ?? null,
                entityType: "dispatch",
                entityId: dispatch.id,
                metadata: {
                  projectId: dispatch?.project?.id ?? null,
                  purchaseOrderId: dispatch?.purchaseOrder?.id ?? null,
                },
                userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
              });
            }
            if (item === "Photos" && activeItem !== "Photos" && dispatch?.id) {
              void trackActivity({
                module: "dispatches",
                action: "dispatch_photos_click",
                actionType: "click",
                userId: null,
                companyId: dispatch?.company?.id ?? dispatch?.project?.company?.id ?? null,
                entityType: "dispatch",
                entityId: dispatch.id,
                metadata: {
                  projectId: dispatch?.project?.id ?? null,
                  purchaseOrderId: dispatch?.purchaseOrder?.id ?? null,
                },
                userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
              });
            }
            setActiveItem(item);
          }}
          notesCount={notesCount}
        />
        <div>{renderActiveSection()}</div>
      </InnerPageWrapper>
      {dispatch?.status !== 5 && (
        <DispatchButtonWrapper>
          <Flex
            as="button"
            onClick={handleIAmHereClick}
            sx={{
              color: "#fff",
              background: themevals.colors.success,
              width: "100%",
              padding: "20px",
              justifyContent: "center",
              fontFamily: "Rubik",
              fontWeight: 600,
              borderRadius: "6px",
              border: "none",
              cursor: onSiteSubmitting ? "default" : "pointer",
              fontSize: "16px",
              opacity: onSiteSubmitting ? 0.75 : 1,
              pointerEvents: onSiteSubmitting ? "none" : "auto",
            }}
          >
            {onSiteSubmitting
              ? "Updating..."
              : dispatch?.status === 4
                ? "MARK AS COMPLETE"
                : "I AM HERE"}
          </Flex>
        </DispatchButtonWrapper>
      )}
      {confirmNotesModalOpen && (
        <BasicModal
          title="Review Notes"
          cancel_click={_cancelConfirmModal}
          cancel_text="Cancel"
          $submitting={false}
          size={"sm"}
          icon={<WarningIcon fill={"#818181"} height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="primary"
              onClick={_goToNotes}
              $submitting={false}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              <SubmitIcon fill={"#fff"} direction={"up"}/>
              {"Go to notes"}
            </BasicButton>
          }
        >
          <P lineHeight={"22px"}>You must first review the project notes before you can let us know you are there.</P>
        </BasicModal>
      )}
      {confirmPhotosModalOpen && (
        <BasicModal
          title="No dispatch photos added!"
          cancel_click={() => setConfirmPhotosModalOpen(false)}
          cancel_text="Cancel"
          $submitting={false}
          size={"sm"}
          icon={<WarningIcon fill={"#818181"} height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="primary"
              onClick={_goToPhotos}
              $submitting={false}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              <SubmitIcon fill={"#fff"} direction={"up"}/>
              {"Go to photos"}
            </BasicButton>
          }
        >
          <P lineHeight={"22px"}>You must first upload photo(s) of the completed job before marking this dispatch as complete.</P>
        </BasicModal>
      )}
    </DispatchPageContainer>
  );
};

export default Page;
